import { useMemo } from 'react';
import moment from 'moment-timezone';

import '../../styles/ArchiveBrigadeSignals.css';
import Table from '../Table/Table';
import Selector from '../CustomElements/Selector';

import { defaultCellRender } from '../../utils/formatValueForHTML';
import useTableColumnSelect from '../../customHook/useTableColumnSelect';

function ArchiveBrigadeSignals({ currentSession, index, t }) {
  // Колонки таблицы
  const columns = useMemo(
    () => [
      {
        Header: '№',
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      {
        Header: t?.signalTime || 'Время',
        accessor: 'time',
        Cell: ({ value }) => {
          // Проверяем, является ли значение валидной датой
          const date = value !== undefined ? moment(value).tz('Europe/Moscow') : null;
          // Форматируем дату в московском часовом поясе
          return date && date.isValid() ? date.format('DD.MM.YYYY HH:mm:ss') : '-';
        },
      },
      {
        Header: t?.signalIndex || 'Номер сигнала',
        accessor: 'index',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: 'Тип сигнала',
        accessor: 'alertType',
        Cell: ({ value }) => {
          switch (value) {
            case 0:
              return t?.signalTypeFollowRC || 'Проследование РЦ';
            case 1:
              return 'ОК';
            case 2:
              return t?.notification1 || 'Оповещение 1';
            case 3:
              return t?.notification2 || 'Оповещение 2';
            case 4:
              return t?.noConnection || 'Потеря связи';
            case 5:
              return t?.alert || 'Тревога';
            case 6:
              return t?.waiting || 'Ожидание';
            case 'start':
              return t?.startWork || 'Начало работы';
            case 'exitRails':
              return t?.exitRails || 'Выход на путь';
            case 'finish':
              return t?.finishWork || 'Завершение работы';

            default:
              return '-';
          }
        },
      },
      {
        Header: t?.direction || 'Направление',
        accessor: 'dir',
        Cell: ({ value }) =>
          value === 1 ? t?.oddDirection || 'Нечетное' : value === 0 ? t?.evenDirection || 'Четное' : '-',
      },
      {
        Header: t?.apkDistance || 'Расстояние по АПК-ДК',
        accessor: 'distance',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.route || 'Контролируемая РЦ',
        accessor: 'routeRailName',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.occupiedRails || 'Занятые РЦ',
        accessor: 'currentRailName',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.warningRails || 'РЦ оповещения',
        accessor: 'warningRails',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.alertRails || 'РЦ тревоги',
        accessor: 'alertRails',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.alertButtonPress || 'Нажатие «Тревога»',
        accessor: 'btn_alert',
        Cell: ({ value }) => (value === 1 ? t?.yes || 'Да' : value === 0 ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.clearButtonPress || 'Нажатие «Путь свободен»',
        accessor: 'btn_clear',
        Cell: ({ value }) => (value === 1 ? t?.yes || 'Да' : value === 0 ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.uippsID || 'ID УИППС',
        accessor: 'uippsId',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.uippsSignalType || 'Сигнал на УИППС',
        accessor: 'uippsAlertType',
        Cell: ({ value }) => {
          switch (Number(value)) {
            case 0:
              return 'ОК';
            case 1:
              return t?.alert || 'Тревога';
            default:
              return '-';
          }
        },
      },
      {
        Header: t?.sadrDistance || 'Расстояние по координатам',
        accessor: 'preciseDistance',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.uippsGPS || 'GPS УИППС',
        accessor: 'uippsGPS',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.uippsGSM || 'GSM УИППС',
        accessor: 'uippsGSM',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.isRestrictOn || 'Ограждение',
        accessor: 'isRestrictOn',
        Cell: ({ value }) => (value === true ? t?.yes || 'Да' : value === false ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.transferRails || 'Перенос (РЦ для переноса)',
        accessor: 'transferRails',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.iurAlertType || 'Сигнал на ИУР',
        accessor: 'iurAlertType',
        Cell: ({ value }) => {
          switch (Number(value)) {
            case 1:
              return 'ОК';
            case 2:
              return t?.notification1 || 'Оповещение 1';
            case 3:
              return t?.notification2 || 'Оповещение 2';
            case 4:
              return t?.noConnection || 'Потеря связи';
            case 5:
              return t?.alert || 'Тревога';
            case 6:
              return t?.waiting || 'Ожидание';
            default:
              return '-';
          }
        },
      },
      {
        Header: t?.iurConn || 'Связь с сервером (ИУР)',
        accessor: 'iurConn',
        Cell: ({ value }) => (value === true ? t?.yes || 'Да' : value === false ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.iurGPS || 'GPS (ИУР)',
        accessor: 'iurGPS',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.iurGSM || 'GSM (ИУР)',
        accessor: 'iurGSM',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.inSafeZone || 'ИУ в БЗ',
        accessor: 'inSafeZone',
        Cell: ({ value }) => (Number(value) === 1 ? t?.yes || 'Да' : Number(value) === 0 ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.safeZoneType || 'Форма БЗ',
        accessor: 'safeZoneType',
        Cell: ({ value }) =>
          Number(value) === 1 ? t?.polygon || 'Полигон' : Number(value) === 0 ? t?.circle || 'Круг' : '-',
      },
      {
        Header: t?.safeZoneWidth || 'Ширина БЗ',
        accessor: 'safeZoneWidth',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.mainKuobzId || 'ID ведущего КУОБЗ',
        accessor: 'mainKuobzId',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.kuobzSignalType || 'Сигнал на КУОБЗ',
        accessor: 'kuobzAlertType',
        Cell: ({ value }) => {
          switch (Number(value)) {
            case 1:
              return 'ОК';
            case 2:
              return t?.notification1 || 'Оповещение 1';
            case 3:
              return t?.notification2 || 'Оповещение 2';
            case 4:
              return t?.noConnection || 'Потеря связи';
            case 5:
              return t?.alert || 'Тревога';
            case 6:
              return t?.waiting || 'Ожидание';
            default:
              return '-';
          }
        },
      },
      {
        Header: t?.kuobzConn || 'Связь с сервером (КУОБЗ)',
        accessor: 'kuobzConn',
        Cell: ({ value }) => (value === true ? t?.yes || 'Да' : value === false ? t?.no || 'Нет' : '-'),
      },
      {
        Header: t?.kuobzGPS || 'GPS (КУОБЗ)',
        accessor: 'kuobzGPS',
        Cell: ({ value }) => defaultCellRender(value),
      },
      {
        Header: t?.kuobzGSM || 'GSM (КУОБЗ)',
        accessor: 'kuobzGSM',
        Cell: ({ value }) => defaultCellRender(value),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSession?.signals, t]
  );

  // eslint-disable-next-line no-unused-vars
  const [visibleColumns, hiddenColumns, columnOptions, selectedColumns, handleColumnSelect] =
    useTableColumnSelect(columns);

  return (
    <>
      <div className="archive-brigade-signals_select-container">
        <Selector
          column="left"
          options={columnOptions}
          onChange={(selectedOption) => handleColumnSelect(selectedOption)}
          filtersBlock={true}
          placeholder={t?.toggleFieldsPlaceholder || 'Показать/скрыть поля'}
          width="100%"
          height="var(--sadr-font-size-60)"
          value={selectedColumns}
          isMulti={true}
          name="columnName"
          isView
          optionSquare
        />
      </div>

      <Table data={currentSession?.signals || []} columns={visibleColumns} hiddenDiv={true} highlightRowIndex={index} />
    </>
  );
}

export default ArchiveBrigadeSignals;
